import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, interval } from 'rxjs';
import { tap, switchMap, startWith } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { MessageService } from 'primeng/api';

export enum NotificationType {
  // User Management
  USER_PENDING_APPROVAL = 'USER_PENDING_APPROVAL',
  USER_ACTIVATED = 'USER_ACTIVATED',
  USER_DEACTIVATED = 'USER_DEACTIVATED',

  // Contract Management
  CONTRACT_CREATED = 'CONTRACT_CREATED',
  CONTRACT_SUBMITTED = 'CONTRACT_SUBMITTED',
  CONTRACT_APPROVED = 'CONTRACT_APPROVED',
  CONTRACT_REJECTED = 'CONTRACT_REJECTED',
  CONTRACT_EXPIRING_SOON = 'CONTRACT_EXPIRING_SOON',
  CONTRACT_EXPIRED = 'CONTRACT_EXPIRED',
  CONTRACT_TERMINATED = 'CONTRACT_TERMINATED',

  // Reservation Management
  RESERVATION_CREATED = 'RESERVATION_CREATED',
  RESERVATION_STATUS_CHANGED = 'RESERVATION_STATUS_CHANGED',
  RESERVATION_EXPIRING_SOON = 'RESERVATION_EXPIRING_SOON',
  RESERVATION_EXPIRED = 'RESERVATION_EXPIRED',
  RESERVATION_CONVERTED = 'RESERVATION_CONVERTED',

  // Interaction Management
  INTERACTION_SCHEDULED = 'INTERACTION_SCHEDULED',
  INTERACTION_REMINDER = 'INTERACTION_REMINDER',
  INTERACTION_UPDATED = 'INTERACTION_UPDATED',
  INTERACTION_COMPLETED = 'INTERACTION_COMPLETED',
  INTERACTION_CANCELLED = 'INTERACTION_CANCELLED',
  INTERACTION_OVERDUE = 'INTERACTION_OVERDUE',

  // Customer Management
  CUSTOMER_CREATED = 'CUSTOMER_CREATED',

  // Subscription & Billing
  SUBSCRIPTION_UPDATED = 'SUBSCRIPTION_UPDATED',
  SUBSCRIPTION_EXPIRING = 'SUBSCRIPTION_EXPIRING',
  PAYMENT_FAILED = 'PAYMENT_FAILED',

  // System Events
  LOGIN_NEW_DEVICE = 'LOGIN_NEW_DEVICE',
  PASSWORD_CHANGED = 'PASSWORD_CHANGED',
  PASSWORD_RESET_REQUESTED = 'PASSWORD_RESET_REQUESTED',

  // Assignments
  TERRITORY_ASSIGNED = 'TERRITORY_ASSIGNED',
  TERRITORY_UNASSIGNED = 'TERRITORY_UNASSIGNED',
  PRODUCT_ASSIGNED = 'PRODUCT_ASSIGNED',
  PRODUCT_UNASSIGNED = 'PRODUCT_UNASSIGNED',

  // System Announcements
  SYSTEM_MAINTENANCE = 'SYSTEM_MAINTENANCE',
  SYSTEM_UPDATE = 'SYSTEM_UPDATE',
  GENERAL_ANNOUNCEMENT = 'GENERAL_ANNOUNCEMENT'
}

export enum NotificationPriority {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  URGENT = 'URGENT'
}

export interface NotificationMetadata {
  entityId?: string;
  entityType?: string;
  actionUrl?: string;
  actionLabel?: string;
  [key: string]: any;
}

export interface Notification {
  _id: string;
  userId: string;
  subscriberId?: string;
  type: NotificationType;
  priority: NotificationPriority;
  title: string;
  message: string;
  metadata?: NotificationMetadata;
  isRead: boolean;
  isViewed: boolean;
  readAt?: Date;
  viewedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface NotificationFilters {
  page?: number;
  limit?: number;
  isRead?: boolean;
  type?: NotificationType;
  priority?: NotificationPriority;
  subscriberId?: string;
}

export interface NotificationResponse {
  success: boolean;
  data: Notification[];
  unreadCount: number;
  page: number;
  limit: number;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private apiUrl = `${environment.apiUrl}/notifications`;
  private unreadCount$ = new BehaviorSubject<number>(0);
  private notifications$ = new BehaviorSubject<Notification[]>([]);

  // Poll for new notifications every 30 seconds
  private readonly POLL_INTERVAL = 30000;

  constructor(
    private http: HttpClient,
    private messageService: MessageService
  ) {
    this.startPolling();
  }

  /**
   * Start polling for unread count and notifications
   */
  private startPolling(): void {
    interval(this.POLL_INTERVAL)
      .pipe(
        startWith(0),
        switchMap(() => this.fetchNotificationsForPolling())
      )
      .subscribe();
  }

  /**
   * Fetch notifications and unread count from server (for polling)
   */
  private fetchNotificationsForPolling(): Observable<NotificationResponse> {
    return this.http.get<NotificationResponse>(`${this.apiUrl}?limit=50`).pipe(
      tap(response => {
        if (response.success) {
          this.notifications$.next(response.data);
          this.unreadCount$.next(response.unreadCount);
        }
      })
    );
  }

  /**
   * Fetch unread count from server
   */
  private fetchUnreadCount(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/unread-count`).pipe(
      tap(response => {
        if (response.success) {
          this.unreadCount$.next(response.count);
        }
      })
    );
  }

  /**
   * Get unread count observable
   */
  getUnreadCount(): Observable<number> {
    return this.unreadCount$.asObservable();
  }

  /**
   * Get notifications observable
   */
  getNotificationsObservable(): Observable<Notification[]> {
    return this.notifications$.asObservable();
  }

  /**
   * Fetch notifications from server
   */
  getNotifications(filters?: NotificationFilters): Observable<NotificationResponse> {
    let params = new HttpParams();

    if (filters) {
      if (filters.page) params = params.set('page', filters.page.toString());
      if (filters.limit) params = params.set('limit', filters.limit.toString());
      if (filters.isRead !== undefined) params = params.set('isRead', filters.isRead.toString());
      if (filters.type) params = params.set('type', filters.type);
      if (filters.priority) params = params.set('priority', filters.priority);
      if (filters.subscriberId) params = params.set('subscriberId', filters.subscriberId);
    }

    return this.http.get<NotificationResponse>(this.apiUrl, { params }).pipe(
      tap(response => {
        if (response.success) {
          this.notifications$.next(response.data);
          this.unreadCount$.next(response.unreadCount);
        }
      })
    );
  }

  /**
   * Mark notification as read
   */
  markAsRead(notificationId: string): Observable<any> {
    return this.http.patch(`${this.apiUrl}/${notificationId}/read`, {}).pipe(
      tap(() => {
        const currentCount = this.unreadCount$.value;
        this.unreadCount$.next(Math.max(0, currentCount - 1));
        
        // Update notification in local state
        const notifications = this.notifications$.value;
        const index = notifications.findIndex(n => n._id === notificationId);
        if (index !== -1) {
          notifications[index].isRead = true;
          notifications[index].readAt = new Date();
          this.notifications$.next([...notifications]);
        }
      })
    );
  }

  /**
   * Mark notification as viewed
   */
  markAsViewed(notificationId: string): Observable<any> {
    return this.http.patch(`${this.apiUrl}/${notificationId}/viewed`, {}).pipe(
      tap(() => {
        // Update notification in local state
        const notifications = this.notifications$.value;
        const index = notifications.findIndex(n => n._id === notificationId);
        if (index !== -1) {
          notifications[index].isViewed = true;
          notifications[index].viewedAt = new Date();
          this.notifications$.next([...notifications]);
        }
      })
    );
  }

  /**
   * Mark all notifications as read
   */
  markAllAsRead(): Observable<any> {
    return this.http.patch(`${this.apiUrl}/mark-all-read`, {}).pipe(
      tap(() => {
        this.unreadCount$.next(0);
        
        // Update all notifications in local state
        const notifications = this.notifications$.value;
        const updatedNotifications = notifications.map(n => ({
          ...n,
          isRead: true,
          readAt: new Date()
        }));
        this.notifications$.next(updatedNotifications);
      })
    );
  }

  /**
   * Delete a notification
   */
  deleteNotification(notificationId: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${notificationId}`).pipe(
      tap(() => {
        // Remove from local state
        const notifications = this.notifications$.value;
        const filtered = notifications.filter(n => n._id !== notificationId);
        this.notifications$.next(filtered);
        
        // Update unread count if necessary
        const deletedNotif = notifications.find(n => n._id === notificationId);
        if (deletedNotif && !deletedNotif.isRead) {
          const currentCount = this.unreadCount$.value;
          this.unreadCount$.next(Math.max(0, currentCount - 1));
        }
      })
    );
  }

  /**
   * Show toast notification
   */
  showToast(notification: Notification): void {
    const severity = this.getPrioritySeverity(notification.priority);
    
    this.messageService.add({
      severity,
      summary: notification.title,
      detail: notification.message,
      life: 5000
    });
  }

  /**
   * Show error toast
   */
  showError(summary: string, detail: string, life: number = 5000): void {
    this.messageService.add({
      severity: 'error',
      summary,
      detail,
      life
    });
  }

  /**
   * Show persistent error toast (doesn't auto-dismiss)
   */
  showPersistentError(summary: string, detail: string): void {
    this.messageService.add({
      severity: 'error',
      summary,
      detail,
      sticky: true,
      closable: true
    });
  }

  /**
   * Show success toast
   */
  showSuccess(summary: string, detail: string, life: number = 3000): void {
    this.messageService.add({
      severity: 'success',
      summary,
      detail,
      life
    });
  }

  /**
   * Show warning toast
   */
  showWarning(summary: string, detail: string, life: number = 4000): void {
    this.messageService.add({
      severity: 'warn',
      summary,
      detail,
      life
    });
  }

  /**
   * Show info toast
   */
  showInfo(summary: string, detail: string, life: number = 3000): void {
    this.messageService.add({
      severity: 'info',
      summary,
      detail,
      life
    });
  }

  /**
   * Get PrimeNG severity from notification priority
   */
  private getPrioritySeverity(priority: NotificationPriority): string {
    switch (priority) {
      case NotificationPriority.URGENT:
        return 'error';
      case NotificationPriority.HIGH:
        return 'warn';
      case NotificationPriority.MEDIUM:
        return 'info';
      case NotificationPriority.LOW:
        return 'success';
      default:
        return 'info';
    }
  }

  /**
   * Get icon class for notification type
   */
  getIconClass(type: NotificationType): string {
    const iconMap: { [key in NotificationType]: string } = {
      // Contracts
      [NotificationType.CONTRACT_CREATED]: 'pi pi-file',
      [NotificationType.CONTRACT_SUBMITTED]: 'pi pi-send',
      [NotificationType.CONTRACT_APPROVED]: 'pi pi-check-circle',
      [NotificationType.CONTRACT_REJECTED]: 'pi pi-times-circle',
      [NotificationType.CONTRACT_EXPIRING_SOON]: 'pi pi-exclamation-triangle',
      [NotificationType.CONTRACT_EXPIRED]: 'pi pi-ban',
      [NotificationType.CONTRACT_TERMINATED]: 'pi pi-times',

      // Reservations
      [NotificationType.RESERVATION_CREATED]: 'pi pi-bookmark',
      [NotificationType.RESERVATION_STATUS_CHANGED]: 'pi pi-refresh',
      [NotificationType.RESERVATION_EXPIRING_SOON]: 'pi pi-clock',
      [NotificationType.RESERVATION_EXPIRED]: 'pi pi-ban',
      [NotificationType.RESERVATION_CONVERTED]: 'pi pi-check',

      // Interactions
      [NotificationType.INTERACTION_SCHEDULED]: 'pi pi-calendar-plus',
      [NotificationType.INTERACTION_REMINDER]: 'pi pi-bell',
      [NotificationType.INTERACTION_UPDATED]: 'pi pi-pencil',
      [NotificationType.INTERACTION_COMPLETED]: 'pi pi-check-square',
      [NotificationType.INTERACTION_CANCELLED]: 'pi pi-times',
      [NotificationType.INTERACTION_OVERDUE]: 'pi pi-exclamation-circle',

      // Customers
      [NotificationType.CUSTOMER_CREATED]: 'pi pi-user-plus',

      // Users
      [NotificationType.USER_PENDING_APPROVAL]: 'pi pi-users',
      [NotificationType.USER_ACTIVATED]: 'pi pi-check-circle',
      [NotificationType.USER_DEACTIVATED]: 'pi pi-ban',

      // Subscriptions
      [NotificationType.SUBSCRIPTION_UPDATED]: 'pi pi-credit-card',
      [NotificationType.SUBSCRIPTION_EXPIRING]: 'pi pi-exclamation-triangle',
      [NotificationType.PAYMENT_FAILED]: 'pi pi-exclamation-circle',

      // System
      [NotificationType.LOGIN_NEW_DEVICE]: 'pi pi-mobile',
      [NotificationType.PASSWORD_CHANGED]: 'pi pi-lock',
      [NotificationType.PASSWORD_RESET_REQUESTED]: 'pi pi-key',

      // Assignments
      [NotificationType.TERRITORY_ASSIGNED]: 'pi pi-map-marker',
      [NotificationType.TERRITORY_UNASSIGNED]: 'pi pi-map',
      [NotificationType.PRODUCT_ASSIGNED]: 'pi pi-box',
      [NotificationType.PRODUCT_UNASSIGNED]: 'pi pi-minus-circle',

      // Announcements
      [NotificationType.SYSTEM_MAINTENANCE]: 'pi pi-wrench',
      [NotificationType.SYSTEM_UPDATE]: 'pi pi-upload',
      [NotificationType.GENERAL_ANNOUNCEMENT]: 'pi pi-megaphone'
    };

    return iconMap[type] || 'pi pi-bell';
  }
}
